// Filters state - including: all filters, selected filters, tileSize, viewMode
export const SUSTAINABILITY_KEYS = [
  "CarbonNeutral",
  "LowEmbodiedCarbon",
  "CradleToCradleGold",
  "CradleToCradleSilver",
  "CradleToCradleBronze",
  "FullyRecyclable",
  "ContainsRecycledContent",
  "FloorScoreCertified",
  "CRIGreenLabelCertified",
  "EPDCertified",
  "HPDCertified",
  "HasDeclareLabel",
  "NSF140Certified"
];
// eslint-disable-next-line max-len
let carbonAzureName = "Sustainability";
const initialFaceWeightValue = [11, 70];

// Initial state
const state = () => ({
  viewMode: "swatch",
  tileSize: "small",
  sortOrder: "OriginalActivationDate",
  isAscendingOrder: "desc",
  searchTerm: null,
  inStock: null,
  filters: [],
  selectedFilters: {
    ...Object.fromEntries(SUSTAINABILITY_KEYS.map((key) => [key, false]))
  },
  filterByFaceWeight: false,
  faceWeightValue: initialFaceWeightValue
});


// Getters
const getters = {
  viewMode: state => {
    return state.viewMode;
  },
  searchTerm: state => {
    return state.searchTerm;
  },
  tileSize: state => {
    return state.tileSize;
  },
  sortOrder: state => {
    return state.sortOrder;
  },
  filters: state => {
    return state.filters;
  },
  selectedFilters: state => {
    return state.selectedFilters;
  },
  inStock: state => {
    return state.inStock;
  },
  isAscendingOrder: state => {
    return state.isAscendingOrder;
  },
  filterByFaceWeight: state => {
    return state.filterByFaceWeight;
  },
  faceWeightValue: state => {
    return state.faceWeightValue;
  },
  searchParams: state => {
    if (!location.search) {
      return;
    }
    const urlParams = new URLSearchParams(location.search);
    let params = {};
    urlParams.forEach((value, key) => {
      if (key !== "CompareProducts") {
        params[key] = value.split(",");
      }
    });
    if (params["searchTerm"]) {
      delete params["searchTerm"];
    }
    if (params["sortOrder"]) {
      delete params["sortOrder"];
    }
    if (params["isAscendingOrder"]) {
      delete params["isAscendingOrder"];
    }
    if (params["faceWeightMin"]) {
      delete params["faceWeightMin"];
      delete params["faceWeightMax"];
    }
    if (params["inStock"]) {
      delete params["inStock"];
    }
    return params;
  },
  carbonFilters: state => {
    let carbonFilters = {};
    if (state.selectedFilters[carbonAzureName]) {
      carbonFilters = Object.assign(
        {},
        ...state.selectedFilters[carbonAzureName].map(() => ({
          CarbonNeutral: state.selectedFilters[carbonAzureName].some(e =>
            e.includes("Neutral")
          ),
          LowEmbodiedCarbon: state.selectedFilters[carbonAzureName].some(e =>
            e.includes("Embodied")
          ),
          CradleToCradleGold: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("Gold")
          ),
          CradleToCradleSilver: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("Silver")
          ),
          CradleToCradleBronze: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("Bronze")
          ),
          GreenhealthApproved: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("Greenhealth")
          ),
          CRIGreenLabelCertified: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("CRI")
          ),
          EPDCertified: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("EPD")
          ),
          HPDCertified: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("HPD")
          ),
          HasDeclareLabel: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("Declare")
          ),
          NSF140Certified: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("NSF")
          ),
          FloorScoreCertified: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("FloorScore")
          ),
          ContainsRecycledContent: state.selectedFilters[carbonAzureName].some( e =>
            e.includes("Recycled")
          ),
          FullyRecyclable: state.selectedFilters[carbonAzureName].some(e =>
            e.includes("Fully")
          )
        }))
      );
    }
    return carbonFilters;
  },
  selections: state => {
    const filtersSelected = { ...state.selectedFilters };
    delete filtersSelected[carbonAzureName];
    const selections = Object.assign(
      {},
      ...Object.entries(filtersSelected).map(([key, value]) => ({
        [key]: Array.isArray(value) ? key == "Location" ? value.join("|") : value.join(",") : ""
      }))
    );
    return selections;
  },
  faceWeightMin: state => {
    return state.filterByFaceWeight ? state.faceWeightValue[0] : null;
  },
  faceWeightMax: state => {
    return state.filterByFaceWeight ? state.faceWeightValue[1] : null;
  }
};

// Actions
const actions = {

  toggleSustainabilityFilter({ commit }, filterName) {
    if (SUSTAINABILITY_KEYS.includes(filterName)) {
      commit("toggleSustainabilityFilter", filterName);
    }
  },
  setViewMode({ commit, state }, viewMode) {
    commit("setViewMode", { viewMode });
  },
  setSearchTerm({ commit, state }, searchTerm) {
    commit("setSearchTerm", { searchTerm });
  },
  setTileSize({ commit, state }, tileSize) {
    commit("setTileSize", { tileSize });
  },
  setSortOrder({ commit, dispatch }, order) {
    const [orderName, orderAsc] = order.split(" ");
    commit("setSortOrder", { sortOrder: orderName });
    if (orderAsc) {
      dispatch("setIsAscendingOrder", orderAsc);
    } else {
      dispatch("setIsAscendingOrder", null);
    }
  },
  onFilterClick({ commit, state, dispatch }, { filterName, option }) {
    if (state.isFetching) {
      return;
    }
    if (state.selectedFilters[filterName]?.includes(option)) {
      dispatch("deselectFilter", { filterName, option });
    } else {
      dispatch("selectFilter", { filterName, option });
    }
  },
  deselectFilter({ commit }, { filterName, option }) {
    commit("deselectFilter", { filterName, option });
  },
  selectFilter({ commit }, { filterName, option }) {
    commit("selectFilter", { filterName, option });
  },
  setInitialSelectedFilters({ commit }, filters) {
    commit("setInitialSelectedFilters", { filters });
  },
  setInStock({ commit }, value) {
    commit("setInStock", { value });
  },
  reSetSelectedFilter({ commit }, value) {
    commit("reSetSelectedFilter", { value });
  },
  resetSearchCriterias({ commit }) {
    commit("resetSearchCriterias");
  },
  updateFilterOptions({ commit }, filters) {
    commit("updateFilterOptions", filters);
  },
  setIsAscendingOrder({ commit }, value) {
    commit("setIsAscendingOrder", { value });
  },
  setFilterByFaceWeight({ commit }, value) {
    commit("setFilterByFaceWeight", { value });
  },
  setFaceWeightValue({ commit }, value) {
    commit("setFaceWeightValue", { value });
  },
  setFiltersFromURL({ commit, dispatch }) {
    if (!location.search) {
      return;
    }
    const urlParams = new URLSearchParams(location.search);
    let params = {};
  
    // Handle sustainability filters
    SUSTAINABILITY_KEYS.forEach((key) => {
      const isActive = urlParams.get(key) === "true";
      commit("setSustainabilityFilter", { key, isActive });
      urlParams.delete(key); // Remove processed keys to avoid duplication
    });
  
    urlParams.forEach((value, key) => {
      if (key !== "CompareProducts") {
        params[key] = value.split(",");
      }
    });
  
    if (params["searchTerm"]) {
      commit("setSearchTerm", { searchTerm: params["searchTerm"][0] });
      delete params["searchTerm"];
    }
    if (params["sortOrder"]) {
      commit("setSortOrder", { sortOrder: params["sortOrder"][0] });
      delete params["sortOrder"];
    }
    if (params["isAscendingOrder"]) {
      commit("setIsAscendingOrder", { value: params["isAscendingOrder"][0] });
      delete params["isAscendingOrder"];
    }
    if (params["faceWeightMin"]) {
      commit("setFilterByFaceWeight", { value: true });
      commit("setFaceWeightValue", {
        value: [...params["faceWeightMin"], ...params["faceWeightMax"]]
      });
      delete params["faceWeightMin"];
      delete params["faceWeightMax"];
    }
    if (params["inStock"]) {
      commit("setInStock", { value: params["inStock"][0] });
      delete params["inStock"];
    }
  
    dispatch("setInitialSelectedFilters", { ...params });
  },  
  setFiltersInURL({ state }, defaultSortOrder) {
    const url = new URL(window.location);
    url.search = "";
  
    // Add sustainability filters to the URL
    SUSTAINABILITY_KEYS.forEach((key) => {
      if (state.selectedFilters[key]) {
        url.searchParams.set(key, "true");
      } else {
        url.searchParams.delete(key);
      }
    });
  
    // Add other filters to the URL
    for (const filt in state.selectedFilters) {
      if (!SUSTAINABILITY_KEYS.includes(filt)) {
        url.searchParams.set(filt, state.selectedFilters[filt]);
      }
    }
  
    if (state.searchTerm) {
      url.searchParams.set("searchTerm", state.searchTerm);
    }
    if (state.filterByFaceWeight) {
      url.searchParams.set("faceWeightMin", state.faceWeightValue[0]);
      url.searchParams.set("faceWeightMax", state.faceWeightValue[1]);
    }
    if (state.sortOrder !== defaultSortOrder) {
      url.searchParams.set("sortOrder", state.sortOrder);
    }
    if (state.isAscendingOrder !== "desc" && state.isAscendingOrder !== null) {
      url.searchParams.set("isAscendingOrder", state.isAscendingOrder);
    } else if (state.isAscendingOrder === null && state.sortOrder === null) {
      url.searchParams.delete("isAscendingOrder");
    }
    if (state.inStock) {
      url.searchParams.set("inStock", state.inStock);
    }
  
    window.history.replaceState({}, "", url);
  },
  clearAllFilters({ commit }) {
    commit("clearAllFilters");
  }
};

// Mutations
const mutations = {
  setViewMode(state, { viewMode }) {
    state.viewMode = viewMode;
  },
  setSearchTerm(state, { searchTerm }) {
    state.searchTerm = searchTerm;
  },
  setTileSize(state, { tileSize }) {
    state.tileSize = tileSize;
  },
  setSortOrder(state, { sortOrder }) {
    state.sortOrder = sortOrder;
  },
  selectFilter(state, { filterName, option }) {
    const newOptions = state.selectedFilters[filterName] ?? [];
    newOptions.push(option);
    state.selectedFilters = {
      ...state.selectedFilters,
      [filterName]: [...newOptions]
    };
  },
  deselectFilter(state, { filterName, option }) {
    let newFilter = state.selectedFilters[filterName];
    newFilter = newFilter.filter(op => op !== option);
    if (!newFilter.length) {
      const selFil = { ...state.selectedFilters };
      delete selFil[filterName];
      state.selectedFilters = {
        ...selFil
      };
    } else {
      state.selectedFilters = {
        ...state.selectedFilters,
        [filterName]: newFilter
      };
    }
  },
  setInitialSelectedFilters(state, { filters }) {
    state.selectedFilters = {};
    Object.entries(filters).map(([key, value]) => {
      state.selectedFilters[key] = [...value];
    });
  },
  updateFilterOptions(state, { filters }) {
    state.filters = [...filters];
    const selected = { ...state.selectedFilters };
    const newSelected = Object.fromEntries(
      Object.entries(selected)
        .map(([key, value]) => {
          const filter = filters.find(f => f.azureFilterName === key);
          return [key, value.filter(i => filter?.options.includes(i))];
        })
        .filter(([k, v]) => v.length)
    );

    if (JSON.stringify(state.selectedFilters) !== JSON.stringify(newSelected)) {
      state.selectedFilters = { ...newSelected };
    }
  },
  setInStock(state, { value }) {
    state.inStock = value;
  },
  setIsAscendingOrder(state, { value }) {
    state.isAscendingOrder = value;
  },
  resetSearchCriterias(state) {
    state.searchTerm = null;
    state.selectedFilters = {};
    state.filterByFaceWeight = false;
    state.inStock = null;
    state.faceWeightValue = initialFaceWeightValue;
  },
  setFilterByFaceWeight(state, { value }) {
    state.filterByFaceWeight = value;
  },
  setFaceWeightValue(state, { value }) {
    state.faceWeightValue = value;
  },
  reSetSelectedFilter(state) {
    state.selectedFilters = {};
  },
  toggleSustainabilityFilter(state, filterName) {
    state.selectedFilters[filterName] = !state.selectedFilters[filterName];
  },
  // Set a sustainability filter directly from the URL
  setSustainabilityFilter(state, { key, isActive }) {
    if (SUSTAINABILITY_KEYS.includes(key)) {
      state.selectedFilters[key] = isActive;
    }
  },
  clearAllFilters(state) {
    SUSTAINABILITY_KEYS.forEach((key) => {
      state.selectedFilters[key] = false;
    });
    state.inStock = null;
    state.filterByFaceWeight = false;
    state.faceWeightValue = [11, 70];
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
